import type { Nullable } from '@ricoh/ria.client.core/types/core/data.type';
import type { Func } from '@ricoh/ria.client.core/types/core/function.type';

import { useCallback, useMemo } from 'react';
import { z } from 'zod';

import type { Batch, DocumentFieldWithValue, ProcessStage } from '~/common/model/api/batch';

import type { ProcessBatchFormField } from '../_process.type';
import type { BatchValidator } from './_validator.type';

import { useFieldDoubleBlindKeyingSchemaCreator } from './schemas/_double-blind-keying';
import { useFieldValidConfidenceSchemaCreator } from './schemas/_ocr';
import { useFieldValidValueSchemaCreator, useFieldValueSchemaCreator } from './schemas/_value';

/**
 * Use Batch Validation.
 *
 * @param {ProcessStage} stage Stage.
 * @param {Batch} batchData Batch data.
 *
 * @returns {Nullable<BatchValidator>} Batch Validation.
 */
export function useBatchValidator(stage: ProcessStage, batchData?: Batch): Nullable<BatchValidator> {
  const createFieldValueSchema = useFieldValueSchemaCreator();
  const createFieldValidValueSchema = useFieldValidValueSchemaCreator();
  const createFieldValidConfidenceSchema = useFieldValidConfidenceSchemaCreator();

  const createDoubleBlindKeyingSchema = useFieldDoubleBlindKeyingSchemaCreator();

  const createValidateSchema = useCallback<Func<[batch: Batch], BatchValidator['validateSchema']>>(
    (batch) => {
      const batchFieldsSchema = z.object(
        batch.fields.reduce<z.ZodRawShape>((prev, { id, name, displayName, type, value }) => {
          prev[id] = z.object({
            isValidValue: createFieldValidValueSchema({
              fieldName: displayName ?? name,
            }),
            value: createFieldValueSchema({
              fieldName: displayName ?? name,
              type,
              metadata: value?.metadata,
            }),
          });

          return prev;
        }, {}) ?? {},
      );

      const documentFieldsSchema =
        stage === 'new' || stage === 'review'
          ? z.array(z.object({}))
          : z.tuple(
              batch.documents.reduce<z.ZodTupleItems | []>((documentPrev, document, index) => {
                documentPrev[index] = z.object(
                  document.fields.reduce<z.ZodRawShape>((prev, { id, name, displayName, type, value }) => {
                    prev[id] = z.object({
                      isValidValue: createFieldValidValueSchema({
                        fieldName: displayName ?? name,
                      }),
                      isValidConfidence: createFieldValidConfidenceSchema({
                        fieldName: displayName ?? name,
                      }),
                      value: createFieldValueSchema({
                        fieldName: displayName ?? name,
                        type,
                        metadata: value?.metadata,
                      }),
                    });

                    return prev;
                  }, {}),
                );

                return documentPrev;
              }, []) ?? [],
            );

      return z.object({
        batchFields: batchFieldsSchema,
        documentFields: documentFieldsSchema,
      }) as unknown as z.ZodSchema<ProcessBatchFormField>;
    },
    [createFieldValidConfidenceSchema, createFieldValidValueSchema, createFieldValueSchema, stage],
  );

  const createDoubleBlindKeyingHandler = useCallback<Func<[batch: Batch], BatchValidator['doubleBlindKeying']>>(
    (batch) => {
      const fields: DocumentFieldWithValue[] = [];

      const documentFieldsSchema = z.tuple(
        batch.documents.reduce<z.ZodTupleItems | []>((documentPrev, document, index) => {
          documentPrev[index] = z.object(
            document.fields.reduce<z.ZodRawShape>((prev, documentField) => {
              const { id, displayName, type, value } = documentField;

              if (!value?.metadata?.condition?.length) {
                return prev;
              }

              const schema = createDoubleBlindKeyingSchema({
                fieldName: displayName,
                type,
                conditions: value.metadata.condition,
              });
              if (schema) {
                fields.push(documentField);
                prev[id] = z.object({
                  value: schema,
                });
              }

              return prev;
            }, {}),
          );

          return documentPrev;
        }, []) ?? [],
      );

      return {
        fields,

        schema: z.object({
          documentFields: documentFieldsSchema,
        }) as unknown as z.ZodSchema<ProcessBatchFormField>,
      };
    },
    [createDoubleBlindKeyingSchema],
  );

  return useMemo<Nullable<BatchValidator>>(() => {
    if (!batchData) {
      return undefined;
    }

    return {
      validateSchema: createValidateSchema(batchData),
      doubleBlindKeying: createDoubleBlindKeyingHandler(batchData),
    };
  }, [batchData, createDoubleBlindKeyingHandler, createValidateSchema]);
}
